import React, { useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '../store';
import { localApi } from '../utils/local-api';
import { Edit, Trash2, Download, Upload, Image as ImageIcon, Music2 } from 'lucide-react';

type MediaType = 'image' | 'audio' | 'all';

const AdminMedia: React.FC = () => {
  // BACKEND_URL is now defined in vite.config.ts globally
  const { token } = useAuthStore();
  // State for filters
  const [mediaType, setMediaType] = useState<MediaType>('all');
  const [level, setLevel] = useState<string>(''); // '' = All levels
  const [limit, setLimit] = useState(50);
  
  // State for uploading
  const [uploadType, setUploadType] = useState<'Picture' | 'Audio'>('Picture');
  const [uploadLevel, setUploadLevel] = useState<string>('HSK1');
  
  useEffect(() => { console.log('AdminMedia v3.1 Loaded'); }, []);

  const [items, setItems] = useState<Array<{ key: string; size: number; uploaded?: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<{ key: string } | null>(null);
  const [replacing, setReplacing] = useState<{ key: string } | null>(null);
  const [newName, setNewName] = useState('');
  const [newFile, setNewFile] = useState<File|null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState<File|null>(null);
  const [uploadName, setUploadName] = useState('');

  // Integrity Check State
  const [checkVersion, setCheckVersion] = useState('2');
  const [checkLevels, setCheckLevels] = useState<number[]>([]);
  const [checkTypes, setCheckTypes] = useState<string[]>([]);
  const [checking, setChecking] = useState(false);
  const [checkResults, setCheckResults] = useState<{ vocab_id: string; path: string; type: string }[] | null>(null);

  // Question Integrity Check State
  const [checkQuestionVersion, setCheckQuestionVersion] = useState('2');
  const [checkQuestionLevels, setCheckQuestionLevels] = useState<number[]>([]);
  const [checkingQuestion, setCheckingQuestion] = useState(false);
  const [checkQuestionResults, setCheckQuestionResults] = useState<{ question_id: string; path: string; type: string }[] | null>(null);

  const toggleCheckLevel = (l: number) => {
    setCheckLevels(prev => prev.includes(l) ? prev.filter(x => x !== l) : [...prev, l]);
  };
  
  const toggleCheckQuestionLevel = (l: number) => {
    setCheckQuestionLevels(prev => prev.includes(l) ? prev.filter(x => x !== l) : [...prev, l]);
  };
  
  const toggleCheckType = (t: string) => {
    setCheckTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  };

  const onCheckIntegrity = async () => {
    if (checkLevels.length === 0 || checkTypes.length === 0) {
      alert('请选择等级和检测类型');
      return;
    }
    setChecking(true);
    setCheckResults(null);
    try {
      const headers: Record<string,string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const res = await fetch(`${BACKEND_URL}/media/check-integrity`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          version: checkVersion,
          levels: checkLevels,
          types: checkTypes
        })
      });
      
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setCheckResults(data.missing || []);
    } catch (e: any) {
      alert('检测失败: ' + e.message);
    } finally {
      setChecking(false);
    }
  };

  const onCheckQuestionIntegrity = async () => {
    if (checkQuestionLevels.length === 0) {
      alert('请选择等级');
      return;
    }
    setCheckingQuestion(true);
    setCheckQuestionResults(null);
    try {
      const headers: Record<string,string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const res = await fetch(`${BACKEND_URL}/media/check-question-integrity`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          version: checkQuestionVersion,
          levels: checkQuestionLevels
        })
      });
      
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setCheckQuestionResults(data.missing || []);
    } catch (e: any) {
      alert('检测失败: ' + e.message);
    } finally {
      setCheckingQuestion(false);
    }
  };

  const fetchList = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Construct prefixes to fetch based on filters
      const prefixes: string[] = [];
      
      const typesToFetch = mediaType === 'all' ? ['Picture', 'Audio'] : 
                           mediaType === 'image' ? ['Picture'] : ['Audio'];
                           
      const levelsToFetch = level === '' ? [''] : [level.toUpperCase()];

      typesToFetch.forEach(t => {
        levelsToFetch.forEach(l => {
          // If l is empty, we just fetch 'Picture/' or 'Audio/' which returns all subfolders
          // If l is 'HSK1', we fetch 'Picture/HSK1/' etc.
          let p = `${t}/`;
          if (l) p += `${l}/`;
          prefixes.push(p);
        });
      });

      // Execute all fetches in parallel
      const promises = prefixes.map(p => localApi.listMedia(p, token || undefined));
      const results = await Promise.all(promises);
      
      // Merge results
      const allItems: typeof items = [];
      results.forEach(res => {
        if (res.objects) {
          allItems.push(...res.objects);
        }
      });
      
      // Deduplicate by key just in case
      const uniqueItems = Array.from(new Map(allItems.map(item => [item.key, item])).values());
      
      // Sort by key
      uniqueItems.sort((a, b) => a.key.localeCompare(b.key));
      
      setItems(uniqueItems);
    } catch (e:any) {
      console.error(e);
      setError(e.message || 'Failed to list media');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [mediaType, level]); // Re-fetch when filters change

  // Filter logic is now mostly handled by fetchList, but we can keep client-side filtering for safety
  // or simple extension checks
  const filtered = useMemo(() => {
    // If we fetched correctly, items should already match the folder structure.
    // We just limit the number of items displayed.
    return items.slice(0, limit);
  }, [items, limit]);

  const onDelete = async (key: string) => {
    if (!confirm('确认删除该媒体？')) return;
    const headers: Record<string,string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`/api/media?key=${encodeURIComponent(key)}`, { method: 'DELETE', headers });
    if (!res.ok) { alert(await res.text()); return; }
    await fetchList();
  };

  const onRename = async () => {
    if (!editing || !newName.trim()) return;
    // For rename, we usually keep the folder and just change the filename part
    // But here we allow arbitrary rename. 
    // To be safe, we might want to extract the path.
    // Let's assume user provides just the filename and we keep the directory.
    const pathParts = editing.key.split('/');
    pathParts.pop(); // remove old filename
    const dir = pathParts.join('/');
    const newKey = `${dir}/${newName.trim()}`;
    
    const body = { key: editing.key, newKey };
    const headers: Record<string,string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch('/api/media/rename', { method: 'POST', headers, body: JSON.stringify(body) });
    if (!res.ok) { alert(await res.text()); return; }
    setEditing(null);
    setNewName('');
    await fetchList();
  };

  const onReplace = async () => {
    if (!replacing || !newFile) return;
    const key = replacing.key;
    const headers: Record<string,string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const fd = new FormData();
    fd.append('key', key);
    fd.append('file', newFile);
    const res = await fetch('/api/media/upload', { method: 'POST', headers, body: fd });
    if (!res.ok) { alert(await res.text()); return; }
    setReplacing(null);
    setNewFile(null);
    await fetchList();
  };

  const onUpload = async () => {
    if (!uploadFile || !uploadName.trim()) return;
    setUploading(true);
    try {
      // Construct key based on upload selectors
      const key = `${uploadType}/${uploadLevel}/${uploadName.trim()}`;
      
      const res = await localApi.uploadMedia(key, uploadFile, token || undefined);
      alert('上传成功：' + res.url);
      setUploadFile(null);
      setUploadName('');
      await fetchList();
    } catch (e:any) {
      alert(e.message || '上传失败');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">媒体库</h1>
              <p className="text-gray-600">管理 Picture/ 和 Audio/ 文件夹下的 HSK 资源</p>
            </div>
            <div className="flex items-center space-x-2">
              <select value={mediaType} onChange={e=>setMediaType(e.target.value as MediaType)} className="border rounded px-3 py-2">
                <option value="all">全部类型</option>
                <option value="image">图片 (Picture/)</option>
                <option value="audio">音频 (Audio/)</option>
              </select>
              <select value={level} onChange={e=>setLevel(e.target.value)} className="border rounded px-3 py-2">
                <option value="">全部等级</option>
                {['hsk1','hsk2','hsk3','hsk4','hsk5','hsk6'].map(l => (<option key={l} value={l}>{l.toUpperCase()}</option>))}
              </select>
              <select value={limit} onChange={e=>setLimit(Number(e.target.value))} className="border rounded px-3 py-2">
                {[20,50,100].map(n => (<option key={n} value={n}>{n}</option>))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          {loading ? (
            <div className="text-gray-600">加载中...</div>
          ) : error ? (
            <div className="text-red-600">{error}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {filtered.map(obj => {
                const base = obj.key.split('/').pop() || '';
                const isImg = /\.(png|jpe?g|webp|gif)$/i.test(base);
                const isAud = /\.(mp3|wav|ogg|m4a)$/i.test(base);
                const url = `${BACKEND_URL}/media/${obj.key}`;
                return (
                  <div key={obj.key} className="border rounded p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm text-gray-700 break-all">{obj.key}</div>
                      <div className="text-xs text-gray-500">{(obj.size/1024).toFixed(1)} KB</div>
                    </div>
                    <div className="mb-3">
                      {isImg ? (
                        <img src={url} alt={base} className="max-h-40 object-contain rounded border" />
                      ) : isAud ? (
                        <audio controls src={url} className="w-full" />
                      ) : (
                        <div className="text-gray-500 flex items-center"><ImageIcon className="w-4 h-4 mr-2" />未知类型</div>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button onClick={()=>{ setEditing({ key: obj.key }); setNewName(base); }} className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"><Edit className="w-4 h-4" /></button>
                      <a href={`${url}?download=1`} className="px-3 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"><Download className="w-4 h-4" /></a>
                      <button onClick={()=>{ setReplacing({ key: obj.key }); setNewFile(null); }} className="px-3 py-2 bg-gray-700 text-white rounded hover:bg-gray-800"><Upload className="w-4 h-4" /></button>
                      <button onClick={()=>onDelete(obj.key)} className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                 <div className="col-span-3 text-center text-gray-500 py-8">没有找到文件</div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-semibold mb-2">上传新媒体</h3>
          <div className="flex items-center space-x-3 flex-wrap gap-y-2">
             <select value={uploadType} onChange={e=>setUploadType(e.target.value as any)} className="border rounded px-3 py-2">
                <option value="Picture">图片 (Picture/)</option>
                <option value="Audio">音频 (Audio/)</option>
             </select>
             <select value={uploadLevel} onChange={e=>setUploadLevel(e.target.value)} className="border rounded px-3 py-2">
                {['HSK1','HSK2','HSK3','HSK4','HSK5','HSK6'].map(l => (<option key={l} value={l}>{l}</option>))}
             </select>
            <input type="text" value={uploadName} onChange={e=>setUploadName(e.target.value)} className="border rounded px-3 py-2 min-w-[300px]" placeholder="文件名（含扩展名）" />
            <input type="file" onChange={e=>{
              const f = e.target.files?.[0] || null;
              setUploadFile(f);
              if (f) setUploadName(f.name);
            }} className="border rounded px-3 py-2" />
            <button onClick={onUpload} disabled={uploading || !uploadFile || !uploadName.trim()} className="px-3 py-2 bg-green-600 text-white rounded">{uploading ? '上传中...' : '上传'}</button>
          </div>
          <p className="text-sm text-gray-500 mt-2">文件将上传至: {uploadType}/{uploadLevel}/{uploadName || 'filename'}</p>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-semibold mb-4">Vocab 媒体完整性检测 (DB vs R2)</h3>
          
          <div className="space-y-4">
            {/* Version Selection */}
            <div>
              <span className="font-medium mr-4">HSK 版本:</span>
              <label className="inline-flex items-center mr-4">
                <input type="radio" checked={checkVersion === '2'} onChange={() => { setCheckVersion('2'); setCheckLevels([]); }} className="mr-2" />
                HSK 2.0
              </label>
              <label className="inline-flex items-center">
                <input type="radio" checked={checkVersion === '3'} onChange={() => { setCheckVersion('3'); setCheckLevels([]); }} className="mr-2" />
                HSK 3.0
              </label>
            </div>

            {/* Level Selection */}
            <div>
              <span className="font-medium mr-4">HSK 等级:</span>
              <div className="flex flex-wrap gap-3 mt-2">
                {Array.from({ length: checkVersion === '2' ? 6 : 9 }, (_, i) => i + 1).map(l => (
                  <label key={l} className="inline-flex items-center bg-gray-50 px-3 py-1 rounded border">
                    <input 
                      type="checkbox" 
                      checked={checkLevels.includes(l)} 
                      onChange={() => toggleCheckLevel(l)} 
                      className="mr-2" 
                    />
                    HSK {l}
                  </label>
                ))}
                <button 
                  onClick={() => setCheckLevels(Array.from({ length: checkVersion === '2' ? 6 : 9 }, (_, i) => i + 1))}
                  className="text-xs text-blue-600 underline ml-2"
                >
                  全选
                </button>
                <button 
                  onClick={() => setCheckLevels([])}
                  className="text-xs text-gray-500 underline ml-2"
                >
                  清空
                </button>
              </div>
            </div>

            {/* Type Selection */}
            <div>
              <span className="font-medium mr-4">检测类型:</span>
              <div className="flex flex-wrap gap-3 mt-2">
                {[
                  { id: 'picture', label: '图片 (picture)' },
                  { id: 'audio', label: '音频 (audio)' },
                  { id: 's_audio', label: '例句音频 (s_audio)' }
                ].map(t => (
                  <label key={t.id} className="inline-flex items-center bg-gray-50 px-3 py-1 rounded border">
                    <input 
                      type="checkbox" 
                      checked={checkTypes.includes(t.id)} 
                      onChange={() => toggleCheckType(t.id)} 
                      className="mr-2" 
                    />
                    {t.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Action */}
            <div>
              <button 
                onClick={onCheckIntegrity} 
                disabled={checking}
                className={`px-4 py-2 rounded text-white font-medium ${checking ? 'bg-gray-400' : 'bg-purple-600 hover:bg-purple-700'}`}
              >
                {checking ? '正在检测...' : '开始检测'}
              </button>
            </div>

            {/* Results */}
            {checkResults && (
              <div className="mt-6 border-t pt-4">
                <h4 className="font-medium mb-2">
                  检测结果: {checkResults.length === 0 ? <span className="text-green-600">全部正常 (无缺失文件)</span> : <span className="text-red-600">发现 {checkResults.length} 个缺失文件</span>}
                </h4>
                
                {checkResults.length > 0 && (
                  <div className="overflow-x-auto border rounded max-h-96 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vocab ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Missing Path (DB Value)</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {checkResults.map((r, idx) => (
                          <tr key={idx}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{r.vocab_id}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{r.type}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-mono">{r.path}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-semibold mb-4">Question 媒体完整性检测 (DB vs R2)</h3>
          
          <div className="space-y-4">
            {/* Version Selection */}
            <div>
              <span className="font-medium mr-4">HSK 版本:</span>
              <label className="inline-flex items-center mr-4">
                <input type="radio" checked={checkQuestionVersion === '2'} onChange={() => { setCheckQuestionVersion('2'); setCheckQuestionLevels([]); }} className="mr-2" />
                HSK 2.0
              </label>
              <label className="inline-flex items-center">
                <input type="radio" checked={checkQuestionVersion === '3'} onChange={() => { setCheckQuestionVersion('3'); setCheckQuestionLevels([]); }} className="mr-2" />
                HSK 3.0
              </label>
            </div>

            {/* Level Selection */}
            <div>
              <span className="font-medium mr-4">HSK 等级:</span>
              <div className="flex flex-wrap gap-3 mt-2">
                {Array.from({ length: checkQuestionVersion === '2' ? 6 : 9 }, (_, i) => i + 1).map(l => (
                  <label key={l} className="inline-flex items-center bg-gray-50 px-3 py-1 rounded border">
                    <input 
                      type="checkbox" 
                      checked={checkQuestionLevels.includes(l)} 
                      onChange={() => toggleCheckQuestionLevel(l)} 
                      className="mr-2" 
                    />
                    HSK {l}
                  </label>
                ))}
                <button 
                  onClick={() => setCheckQuestionLevels(Array.from({ length: checkQuestionVersion === '2' ? 6 : 9 }, (_, i) => i + 1))}
                  className="text-xs text-blue-600 underline ml-2"
                >
                  全选
                </button>
                <button 
                  onClick={() => setCheckQuestionLevels([])}
                  className="text-xs text-gray-500 underline ml-2"
                >
                  清空
                </button>
              </div>
            </div>

            {/* Action */}
            <div>
              <button 
                onClick={onCheckQuestionIntegrity} 
                disabled={checkingQuestion}
                className={`px-4 py-2 rounded text-white font-medium ${checkingQuestion ? 'bg-gray-400' : 'bg-purple-600 hover:bg-purple-700'}`}
              >
                {checkingQuestion ? '正在检测...' : '开始检测'}
              </button>
            </div>

            {/* Results */}
            {checkQuestionResults && (
              <div className="mt-6 border-t pt-4">
                <h4 className="font-medium mb-2">
                  检测结果: {checkQuestionResults.length === 0 ? <span className="text-green-600">全部正常 (无缺失文件)</span> : <span className="text-red-600">发现 {checkQuestionResults.length} 个缺失文件</span>}
                </h4>
                
                {checkQuestionResults.length > 0 && (
                  <div className="overflow-x-auto border rounded max-h-96 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Question ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Missing Path (DB Value)</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {checkQuestionResults.map((r, idx) => (
                          <tr key={idx}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{r.question_id}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{r.type}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-mono">{r.path}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center">
          <div className="bg-white rounded shadow p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">重命名</h3>
            <p className="text-sm text-gray-600 break-all mb-2">原路径：{editing.key}</p>
            <input value={newName} onChange={e=>setNewName(e.target.value)} className="w-full border rounded px-3 py-2 mb-4" placeholder="新文件名" />
            <div className="flex justify-end space-x-2">
              <button onClick={()=>{ setEditing(null); setNewName(''); }} className="px-3 py-2 border rounded">取消</button>
              <button onClick={onRename} className="px-3 py-2 bg-blue-600 text-white rounded">保存</button>
            </div>
          </div>
        </div>
      )}

      {replacing && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center">
          <div className="bg-white rounded shadow p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">替换内容</h3>
            <p className="text-sm text-gray-600 break-all mb-2">路径：{replacing.key}</p>
            <input type="file" onChange={e=>setNewFile(e.target.files?.[0] || null)} className="w-full border rounded px-3 py-2 mb-4" />
            <div className="flex justify-end space-x-2">
              <button onClick={()=>{ setReplacing(null); setNewFile(null); }} className="px-3 py-2 border rounded">取消</button>
              <button onClick={onReplace} disabled={!newFile} className="px-3 py-2 bg-gray-800 text-white rounded">替换</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMedia;
